/**
 * Forms Settings — the HOST editor's pure rules: the four tabs, their order, the per-tab state and which tab the save bar blames. Pure.
 * Plan: docs/plans/2026-10-20-forms-settings-plan.md §0 (D1, D9, D10, D15a), §2.1, §2.6.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE, with NAMED exports only — cypress/node-tests/forms-settings-host.test.js executes it. (It is NOT one of the
 *    three WP0 files in this folder that the twin tests concatenate; those are formLink.js / newPatient.js / medHx.js. Names here may repeat theirs.)
 *
 * ── THE TAB CONTRACT (WP2b implements it in NewPatientsTab.vue / MedHxTab.vue) ────────────────────────────────────────────────────────
 * ProcedureFormsEditor (host) renders the two sending tabs ALWAYS-MOUNTED (v-show, so a half-edited tab survives a tab switch) and
 * `provide`s `formsSettingsHost` = { register(key, vm), unregister(key, vm) }. A sending tab:
 *
 *   inject: ['formsSettingsHost']
 *   created()       this.formsSettingsHost.register('newPatients' | 'medHx', this)
 *   beforeDestroy() this.formsSettingsHost.unregister('newPatients' | 'medHx', this)
 *
 * and exposes, on the component instance (the host READS these reactively — computed properties are ideal — and CALLS the methods):
 *
 *   dirty       Boolean   true ONLY after the office edited THIS tab. Prefilled defaults NEVER dirty a tab (D15a) — an office that opens
 *                         Forms Settings for post-ops and presses Save must not touch New Patients.
 *   errorCount  Number    the problems that would make the server refuse a save of the working copy (the WP0 validators). Counted by the
 *                         host only while the tab is dirty, so an untouched tab full of "pick a form" prefills never blocks another tab's Save.
 *   saving      Boolean   a request of this tab's own is in flight (its switch card's confirm, or save()).
 *   validate()  -> Number reveals the tab's inline errors (a Save attempt) and returns errorCount.
 *   save()      -> Promise<{ ok:true } | { ok:false, message:String }>
 *                         writes ONLY this tab's content through saveFormsSettingsSection (src/api/formsSettings.js). ⛔ It NEVER sends
 *                         `enabled` / `activated` — those travel only in the SendSwitchCard's own confirm request. It does not throw; a
 *                         server refusal comes back as { ok:false, message: <the server's sentence> } and the tab shows its own detail.
 *   reset()     -> void   back to what the server has (or the prefilled defaults); `dirty` becomes false.
 *
 * The host keeps Procedure mappings ('map') and Post-op message ('message') itself (they already live there); the same four-key state
 * object describes all four.
 */

/** Internal tab keys, in the order the tabs are drawn. ('map' and 'message' are the keys the shipped two-tab editor already uses.) */
export const TAB_KEYS = Object.freeze(['newPatients', 'medHx', 'map', 'message']);

export const TAB_LABELS = Object.freeze({
  newPatients: 'New Patients',
  medHx: 'Medical History Update',
  map: 'Procedure mappings',
  message: 'Post-op message',
});

/** Save walks the dirty tabs in THIS order (rows first: the post-op switch's gate reads the stored rows), stopping at the first refusal. */
export const SAVE_ORDER = Object.freeze(['map', 'message', 'newPatients', 'medHx']);

/** The `initialTab` prop / `?settings=` query values -> the internal key. */
export const INITIAL_TAB_KEYS = Object.freeze({
  newPatients: 'newPatients',
  medHx: 'medHx',
  mappings: 'map',
  postOp: 'message',
});

/** The tab the editor opens on: the shipped two-tab editor keeps 'map'; Forms Settings opens on New Patients (D9). */
export function resolveInitialTab(initialTab, formsSettings) {
  if (!formsSettings) return 'map';
  return Object.prototype.hasOwnProperty.call(INITIAL_TAB_KEYS, initialTab) ? INITIAL_TAB_KEYS[initialTab] : 'newPatients';
}

/**
 * `?settings=<tab>` on the Forms page -> an `initialTab` value, or null when the query does not name a tab (the page then opens nothing).
 * Accepts the four prop values only (newPatients | medHx | mappings | postOp); a repeated query (array) uses its first value.
 */
export function settingsTabFromQuery(value) {
  const v = Array.isArray(value) ? value[0] : value;
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(INITIAL_TAB_KEYS, v) ? v : null;
}

/** One tab's state. `errors` is a COUNT. */
export function emptyTabState() {
  return { loaded: false, dirty: false, errors: 0, saving: false };
}

function stateOf(states, key) {
  return states && typeof states === 'object' && states[key] ? states[key] : emptyTabState();
}

/** Any tab edited ⇒ the editor has unsaved work (the save bar, Reset, the discard dialog). */
export function anyDirty(states) {
  return TAB_KEYS.some((key) => stateOf(states, key).dirty === true);
}

/** Any tab with a request in flight. */
export function anySaving(states) {
  return TAB_KEYS.some((key) => stateOf(states, key).saving === true);
}

/** The dirty tabs, in the order Save writes them. */
export function dirtyInSaveOrder(states) {
  return SAVE_ORDER.filter((key) => stateOf(states, key).dirty === true);
}

/**
 * The tab the save bar names: the FIRST tab (in the order they are drawn) that is dirty AND has problems — a tab nobody edited never
 * counts, however many prefilled "pick a form" problems it holds (D15a).
 * @returns {{ key:string, label:string, count:number }|null}
 */
export function firstIssueTab(states) {
  for (let i = 0; i < TAB_KEYS.length; i += 1) {
    const key = TAB_KEYS[i];
    const s = stateOf(states, key);
    const count = Number.isInteger(s.errors) && s.errors > 0 ? s.errors : 0;
    if (s.dirty === true && count > 0) return { key, label: TAB_LABELS[key], count };
  }
  return null;
}
